import {
  useCallback,
  useRef,
  useState,
  type RefObject,
  type ClipboardEvent,
  type DragEvent,
} from "react";
import { uploadAsset } from "../api.js";
import { invalidate } from "../data/useFetch.js";
import { markdownFor } from "./assets.js";

// Monotonic id so each in-flight upload has a unique placeholder token to find
// and replace once it resolves — even if several upload at once.
let seq = 0;

/**
 * Drag-and-drop / paste upload for a markdown `<textarea>`. Dropping or
 * pasting files inserts an `![uploading …]` placeholder at the cursor, uploads
 * each file, and swaps the placeholder for the final link (or drops it on
 * failure). GitHub-comment style.
 *
 * Images embed, everything else becomes a plain link — see editor/assets.ts.
 *
 * `setValue` takes a functional updater so replacements compose with whatever
 * the user keeps typing.
 */
export function useAssetDrop(
  ref: RefObject<HTMLTextAreaElement | null>,
  setValue: (updater: (prev: string) => string) => void,
  repo: string,
  pagePath: string,
) {
  const upload = useCallback(
    (files: File[], cursor: number) => {
      if (!files.length) return;

      const jobs = files.map((file) => ({
        file,
        token: `![uploading ${file.name}…](#dw-upload-${++seq})`,
      }));

      // Insert every placeholder at the cursor, each on its own line.
      const block = jobs.map((j) => j.token).join("\n");
      setValue((prev) => {
        const at = Math.min(cursor, prev.length);
        const before = prev.slice(0, at);
        const after = prev.slice(at);
        const lead = before && !before.endsWith("\n") ? "\n" : "";
        const trail = after && !after.startsWith("\n") ? "\n" : "";
        return `${before}${lead}${block}${trail}${after}`;
      });

      for (const { file, token } of jobs) {
        uploadAsset(repo, file, pagePath)
          .then(({ url, isImage }) => {
            // Images keep their filename stem as alt text — it's the only
            // description we have, and it beats an empty alt.
            const label = isImage ? file.name.replace(/\.[^.]+$/, "") : file.name;
            setValue((prev) => prev.replace(token, markdownFor(label, url, isImage)));
            // The new asset file changes the tree + git status.
            invalidate("tree");
            invalidate("commit");
          })
          .catch((e: unknown) => {
            setValue((prev) => prev.replace(token, ""));
            window.alert(
              `Upload failed: ${e instanceof Error ? e.message : String(e)}`,
            );
          });
      }
    },
    [setValue, repo, pagePath],
  );

  const cursorOf = useCallback(
    () => ref.current?.selectionStart ?? ref.current?.value.length ?? 0,
    [ref],
  );

  // Whether a file is being dragged over the drop zone, for the dim overlay.
  // enter/leave bubble and fire per child boundary, so count depth rather than
  // toggle — otherwise crossing from the article into the textarea flickers it.
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);

  const hasFiles = (e: DragEvent<HTMLElement>): boolean =>
    Array.from(e.dataTransfer?.items ?? []).some((i) => i.kind === "file");

  const onDragEnter = useCallback((e: DragEvent<HTMLElement>) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    depth.current += 1;
    setDragging(true);
  }, []);

  const onDragLeave = useCallback(() => {
    if (depth.current === 0) return;
    depth.current -= 1;
    if (depth.current === 0) setDragging(false);
  }, []);

  // Without preventDefault the browser opens dropped files instead of letting
  // onDrop fire. Only intercept file drags so text drag-and-drop is unaffected.
  const onDragOver = useCallback((e: DragEvent<HTMLElement>) => {
    if (hasFiles(e)) e.preventDefault();
  }, []);

  const onDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      depth.current = 0;
      setDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (!files.length) return;
      e.preventDefault();
      e.stopPropagation();
      upload(files, cursorOf());
    },
    [upload, cursorOf],
  );

  const onPaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(e.clipboardData?.files ?? []);
      if (!files.length) return;
      e.preventDefault();
      upload(files, cursorOf());
    },
    [upload, cursorOf],
  );

  return { onDrop, onDragOver, onDragEnter, onDragLeave, onPaste, dragging };
}
